import { FormAuthenticatedData, ReCaptchaSetting } from 'backend';
import { postInputData, PostInputDataPayload } from 'fetcher';
import { InputData } from 'shared';
import {
  verifyFileUploadState,
  VERIFY_FILE_UPLOAD_STATE_RESULT_TYPE,
} from '../../pages/shared';
import { useFileUpload, useInputData } from '../../store';
import {
  getReCaptchaRequestPayload,
  handleCommonSubmitError,
  SubmissionResult,
  SUBMISSION_RESULT_TYPE,
} from '../shared';
import { useReCaptcha } from '../useReCaptcha';

export type UseSubmitInputDataPayload = {
  endpoint: PostInputDataPayload['endpoint'];
  inputData?: InputData;
  authentication?: FormAuthenticatedData;
};

export type UseSubmitInputDataReturnType = {
  submit: (payload: UseSubmitInputDataPayload) => Promise<SubmissionResult>;
};

type Payload = {
  reCaptchaSetting: ReCaptchaSetting | undefined;
};

export const useSubmitInputData = ({
  reCaptchaSetting,
}: Payload): UseSubmitInputDataReturnType => {
  const inputDataFromStore = useInputData();
  const fileUploadState = useFileUpload();
  const { executeReCaptcha } = useReCaptcha(reCaptchaSetting);

  const submit = async ({
    endpoint,
    inputData: updateInputData,
    authentication: currentAuthentication,
  }: UseSubmitInputDataPayload) => {
    const fileUploadStateResult = verifyFileUploadState({
      fileUploadState,
    });
    if (
      fileUploadStateResult.type === VERIFY_FILE_UPLOAD_STATE_RESULT_TYPE.error
    ) {
      return {
        type: SUBMISSION_RESULT_TYPE.fileUploadValidationError,
        errorMessage: fileUploadStateResult.errorMessage,
        unrecoverable: fileUploadStateResult.unrecoverable,
      };
    }
    const reCaptchaResult = await executeReCaptcha();
    const reCaptcha = getReCaptchaRequestPayload(reCaptchaResult);
    const payload: PostInputDataPayload['payload'] = {
      inputData: updateInputData ?? inputDataFromStore,
      currentUrl: location.href,
      reCaptcha,
    };
    try {
      const {
        page,
        inputData,
        reCaptcha,
        error,
        trackingSetting,
        authentication,
        externalMeasurementTag,
        apiEndpoint,
        fileUpload,
      } = await postInputData({
        endpoint,
        payload,
        authentication: currentAuthentication,
      });
      return {
        type: SUBMISSION_RESULT_TYPE.success,
        formPage: page,
        inputData,
        reCaptchaSetting: reCaptcha,
        error,
        trackingSetting,
        authentication,
        externalMeasurementTag,
        apiEndpoint,
        fileUploadSetting: fileUpload,
      };
    } catch (e: unknown) {
      return handleCommonSubmitError(e);
    }
  };

  return {
    submit,
  };
};
